import { useQuery } from "@tanstack/react-query";
import { post } from "./request";

// Shared payload type for all dashboard endpoints
export interface ComplianceDashboardPayload {
  company_id?: number[];
  associate_company_id?: number[];
  state_id?: number[];
  location_id?: number[];
  vertical_id?: number[];
  department_id?: number[];
  owner_id?: number[];
  manager_id?: number[];
  financial_year?: string;
  year_eq?: number;          // sent instead of financial_year when Full Year is selected
  quarter?: string;
  month_year?: string;   // "YYYY-MM"  — from Month & Year picker in More Filters
  due_date?: string;     // "YYYY-MM-DD" — from Due Date picker in More Filters
  activity_type?: string; // Activity Type tab filter (e.g. "Factory", "Returns")
}

function postDashboard(endpoint: string, payload: ComplianceDashboardPayload) {
  return post(`/api/v1/compliances/${endpoint}`, payload);
}

// ── API 1: Overview ────────────────────────────────────────────────────────────
export function useGetComplianceDashboardOverview(payload: ComplianceDashboardPayload, enabled = true) {
  const { data, isFetching, refetch } = useQuery(
    ['complianceDashboardOverview', payload],
    () => postDashboard('dashboard_overview', payload),
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled }
  );
  const result = (data as any) && (data as any).data;
  return {
    statusCounts: (result && result.status_counts) || null,
    activityTypeCounts: (result && result.activity_type_counts) || ({} as Record<string, number>),
    isFetching,
    refetch,
  };
}

// ── API 2: Score Cards ─────────────────────────────────────────────────────────
export function useGetComplianceDashboardScoreCards(payload: ComplianceDashboardPayload, enabled = true) {
  const { data, isFetching, refetch } = useQuery(
    ['complianceDashboardScoreCards', payload],
    () => postDashboard('dashboard_score_cards', payload),
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled }
  );
  const result = (data as any) && (data as any).data;
  return {
    scoreCards: result || null,
    isFetching,
    refetch,
  };
}

// ── API 3: Category Breakdown ──────────────────────────────────────────────────
export function useGetComplianceDashboardCategoryBreakdown(payload: ComplianceDashboardPayload, enabled = true) {
  const { data, isFetching, refetch } = useQuery(
    ['complianceDashboardCategoryBreakdown', payload],
    () => postDashboard('dashboard_category_breakdown', payload),
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled }
  );
  const result = (data as any) && (data as any).data;
  return {
    categories: (result && result.categories) || [],
    isFetching,
    refetch,
  };
}

// ── API 4: Location Breakdown ──────────────────────────────────────────────────
export function useGetComplianceDashboardLocationBreakdown(payload: ComplianceDashboardPayload, enabled = true) {
  const { data, isFetching, refetch } = useQuery(
    ['complianceDashboardLocationBreakdown', payload],
    () => postDashboard('dashboard_location_breakdown', payload),
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled }
  );
  const result = (data as any) && (data as any).data;
  return {
    total: (result && result.total) || 0,
    locations: (result && result.locations) || [],
    isFetching,
    refetch,
  };
}

// ── API 5a: Owner Accountability (manager login — grouped by owner) ────────────
export function useGetComplianceDashboardOwnerAccountability(payload: ComplianceDashboardPayload, enabled = true) {
  const { data, isFetching, refetch } = useQuery(
    ['complianceDashboardOwnerAccountability', payload],
    () => postDashboard('dashboard_owner_accountability', payload),
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled }
  );
  const result = (data as any) && (data as any).data;
  return {
    owners: (result && result.owners) || [],
    isFetching,
    refetch,
  };
}

// ── API 5b: Manager Accountability (owner login — grouped by manager) ──────────
export function useGetComplianceDashboardManagerAccountability(payload: ComplianceDashboardPayload, enabled = true) {
  const { data, isFetching, refetch } = useQuery(
    ['complianceDashboardManagerAccountability', payload],
    () => postDashboard('dashboard_manager_accountability', payload),
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled }
  );
  const result = (data as any) && (data as any).data;
  return {
    managers: (result && result.managers) || [],
    isFetching,
    refetch,
  };
}

// ── API 5c: Compliance Accountability Pairs (escalation login) ────────────────
// POST /api/v1/compliances/dashboard_compliance_accountability
// Returns every unique owner+manager pair with status breakdown.
export function useGetComplianceDashboardPairsAccountability(payload: ComplianceDashboardPayload, enabled = true) {
  const { data, isFetching, refetch } = useQuery(
    ['complianceDashboardPairsAccountability', payload],
    () => postDashboard('dashboard_compliance_accountability', payload),
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled }
  );
  const result = (data as any) && (data as any).data;
  return {
    pairs: (result && result.pairs) || [],
    isFetching,
    refetch,
  };
}

// ── API 6: Calendar Activities ────────────────────────────────────────────────
// Separate from the main payload — financial_year / quarter are intentionally excluded.
export function useGetComplianceDashboardCalendarActivities(
  basePayload: ComplianceDashboardPayload,
  dueDate: string,   // "YYYY-MM-DD"
  enabled = true
) {
  const payload = {
    due_date:             dueDate,
    company_id:           basePayload.company_id,
    associate_company_id: basePayload.associate_company_id,
    state_id:             basePayload.state_id,
    location_id:          basePayload.location_id,
    vertical_id:          basePayload.vertical_id,
    department_id:        basePayload.department_id,
    owner_id:             basePayload.owner_id,
    manager_id:           basePayload.manager_id,
  };
  const { data, isFetching, refetch } = useQuery(
    ['complianceDashboardCalendarActivities', payload],
    () => post('/api/v1/compliances/dashboard_calendar_activities', payload as any),
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled: enabled && !!dueDate }
  );
  const result = (data as any) && (data as any).data;
  return {
    items:      (result && result.items)       || [],
    totalCount: (result && result.total_count) || 0,
    isFetching,
    refetch,
  };
}

// ── API 8: Calendar Month ─────────────────────────────────────────────────────
// Returns per-day status counts for a whole month.
// Only entity filters are forwarded (no financial_year / quarter).
export interface CalendarMonthDate {
  date:             string;   // "YYYY-MM-DD"
  total:            number;
  non_compliant:    number;
  upcoming:         number;
  approval_pending: number;
  rejected:         number;
  dominant_status:  'non_compliant' | 'upcoming' | 'approval_pending' | 'rejected';
}

export function useGetComplianceDashboardCalendarMonth(
  basePayload: ComplianceDashboardPayload,
  year: number,
  month: number,
  enabled = true
) {
  const payload = {
    year,
    month,
    company_id:           basePayload.company_id,
    associate_company_id: basePayload.associate_company_id,
    state_id:             basePayload.state_id,
    location_id:          basePayload.location_id,
    vertical_id:          basePayload.vertical_id,
    department_id:        basePayload.department_id,
    owner_id:             basePayload.owner_id,
    manager_id:           basePayload.manager_id,
  };
  const { data, isFetching } = useQuery(
    ['complianceDashboardCalendarMonth', payload],
    () => post('/api/v1/compliances/dashboard_calendar_month', payload as any),
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled: enabled && !!year && !!month }
  );
  const result = (data as any) && (data as any).data;
  return {
    monthDates: ((result && result.dates) || []) as CalendarMonthDate[],
    isFetching,
  };
}

// ── API 9: Penalty Metrics ─────────────────────────────────────────────────────
export function useGetComplianceDashboardPenaltyMetrics(payload: ComplianceDashboardPayload, enabled = true) {
  const { data, isFetching, refetch } = useQuery(
    ['complianceDashboardRiskExposure', payload],
    () => postDashboard('dashboard_risk_exposure', payload),
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled }
  );
  const result = (data as any) && (data as any).data;
  return {
    penaltyMetrics: result || null,
    isFetching,
    refetch,
  };
}

// ── API 8: Risk Watchlist ──────────────────────────────────────────────────────
export function useGetComplianceDashboardRiskWatchlist(
  payload: ComplianceDashboardPayload,
  page: number,
  perPage: number,
  enabled = true
) {
  const fullPayload = { ...payload, page, per_page: perPage };
  const { data, isFetching, refetch } = useQuery(
    ['complianceDashboardRiskWatchlist', fullPayload],
    () => postDashboard('dashboard_risk_watchlist', fullPayload as any),
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled, keepPreviousData: true }
  );
  const result = (data as any) && (data as any).data;
  return {
    items: (result && result.items) || [],
    totalCount: (result && result.total_count) || 0,
    criticalHighCount: (result && result.critical_high_count) || 0,
    totalPages: (result && result.total_pages) || 0,
    isFetching,
    refetch,
  };
}
